import { VorgangProzessSchritt } from '@core/domain';
import { format } from 'date-fns';
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { VORGANG_TYP_TRANSLATION_MAP } from '@/features/vorgangHistorie/utils/common';
import { getStatusMapping } from '@/lib/utils';
import type { VorgangDetail } from '@/types';

interface VorgangInformationProps {
  vorgang: VorgangDetail;
}

export const VorgangInformation: React.FC<VorgangInformationProps> = ({
  vorgang,
}) => {
  const isDeleted = vorgang.deletedAt != null;

  const letzteAbarbeitung = vorgang.abarbeitungen.find(
    (a) => a.id === vorgang.letzteAbarbeitungId,
  );

  const fehler = letzteAbarbeitung?.schritte.find((s) => s.fehler)?.fehler;

  const powercloudEventName =
    vorgang.type === 'POWERCLOUD_EVENT'
      ? letzteAbarbeitung?.schritte.find(
          (s) =>
            (s.schritt as VorgangProzessSchritt) ===
            VorgangProzessSchritt.TRANSFORMATION,
          // @ts-expect-error - This should be a powercloud event
        )?.initialPayload?.powercloudEvent?.EventName
      : null;

  return (
    <div
      className={`bg-white border border-gray-200 rounded-xl shadow-sm p-8 space-y-6 ${isDeleted ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">
          Vorgang-Informationen
        </h2>
        {isDeleted && (
          <Badge variant="destructive" className="text-xs">
            Gelöscht
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="space-y-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Vorgang Typ
          </span>
          <p
            className="text-sm font-mono text-gray-900 bg-gray-50 px-3 py-2 rounded-md border overflow-hidden text-ellipsis"
            title={
              powercloudEventName ?? VORGANG_TYP_TRANSLATION_MAP[vorgang.type]
            }
          >
            {powercloudEventName ?? VORGANG_TYP_TRANSLATION_MAP[vorgang.type]}
          </p>
        </div>

        <div className="space-y-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Letzte Abarbeitung ID
          </span>
          <p className="text-sm font-mono text-gray-900 bg-gray-50 px-3 py-2 rounded-md border">
            {vorgang.letzteAbarbeitungId || '-'}
          </p>
        </div>

        <div className="space-y-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Externe ID
          </span>
          <p className="text-sm font-mono text-gray-900 bg-gray-50 px-3 py-2 rounded-md border">
            {vorgang.externeId}
          </p>
        </div>

        <div className="space-y-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Auftragsnummer
          </span>
          <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border">
            {vorgang.auftragsnummer || '-'}
          </p>
        </div>

        <div className="space-y-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Vertragsnummer
          </span>
          <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border">
            {vorgang.vertragsnummer || '-'}
          </p>
        </div>

        <div className="space-y-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Erstellt
          </span>
          <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md border">
            {format(vorgang.erstelltDatum, 'dd.MM.yyyy HH:mm:ss')}
          </p>
        </div>

        <div className="space-y-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Status
          </span>
          <div className="flex gap-2">
            {(() => {
              const { text, variant } = getStatusMapping(
                letzteAbarbeitung.status,
                isDeleted,
              );
              return (
                <Badge variant={variant} className="text-xs font-medium">
                  {text}
                </Badge>
              );
            })()}
          </div>
        </div>

        {isDeleted && vorgang.deletedBy && (
          <div className="space-y-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Gelöscht von
            </span>
            <p className="text-sm text-gray-900 bg-red-50 px-3 py-2 rounded-md border border-red-200">
              {vorgang.deletedBy} -{' '}
              {vorgang.deletedAt
                ? format(new Date(vorgang.deletedAt), 'dd.MM.yyyy HH:mm')
                : ''}
            </p>
          </div>
        )}
      </div>

      {fehler && (
        <div className="border-t border-gray-200 pt-6">
          <div className="space-y-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Fehlerbezeichnung
            </span>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-red-600 bg-red-50 px-3 py-2 rounded-md border border-red-200 whitespace-pre-wrap">
                {fehler?.message ?? 'Unbekannter Fehler'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
